'use client';

import React, { createContext, useCallback, useContext, useState } from 'react';
import useEscape from '@/lib/useEscape';

type ConfirmOptions = {
  title?: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
};

type ConfirmContextType = {
  openConfirm: (options?: ConfirmOptions) => Promise<boolean>;
};

const ConfirmContext = createContext<ConfirmContextType | null>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [opts, setOpts] = useState<ConfirmOptions>({});
  const [resolver, setResolver] = useState<((value: boolean) => void) | null>(null);

  const openConfirm = useCallback((options: ConfirmOptions = {}) => {
    setOpts(options);
    setIsOpen(true);
    return new Promise<boolean>((resolve) => {
      setResolver(() => resolve);
    });
  }, []);

  const handleClose = (result: boolean) => {
    setIsOpen(false);
    if (resolver) resolver(result);
    setResolver(null);
  };

  // Close on Escape
  useEscape(() => handleClose(false), isOpen);

  return (
    <ConfirmContext.Provider value={{ openConfirm }}>
      {children}

      {isOpen && (
        <div
          className="fixed inset-0 z-60 flex items-center justify-center bg-black/50"
          onClick={() => handleClose(false)}
        >
          {/* Persistent close button top-right of viewport so it's visible while modal content scrolls */}
          <button
            aria-label="Close dialog"
            onClick={() => handleClose(false)}
            className="fixed top-4 right-4 z-70 bg-white rounded-full p-2 shadow hover:bg-gray-100 text-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-300"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <div
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4 dark:bg-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-2">{opts.title || 'Are you sure?'}</h3>
            {opts.description && (
              <p className="text-sm text-gray-600 mb-4 dark:text-gray-300">{opts.description}</p>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => handleClose(false)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded dark:bg-gray-700 dark:hover:bg-gray-600"
              >
                {opts.cancelLabel || 'Cancel'}
              </button>
              <button
                onClick={() => handleClose(true)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded"
              >
                {opts.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx.openConfirm;
}
